import React, { useEffect, useRef, useState } from "react";
import { WireLoyaltyLink, formatMiles } from "../../services/mockLoyalty";
import { applyWireTraveler } from "../../hooks/linkedTravelerStore";
import { unlinkLoyaltyWithSession } from "../../services/demoAirlineApi";

interface LoyaltyMemberChipProps {
    firstName: string;
    link: WireLoyaltyLink;
}

/**
 * Header member chip for the derived loyalty session (MOVMO-541 Layer 3) —
 * only rendered once sessionActive is true (see useLoyaltySession). `link`
 * is wire-sourced data (MOVMO-567) — never fixture/theater data. Compact
 * by design: the nav already wraps at 1280px (see Header.tsx's comment on
 * space-x-6), so this must stay one line.
 *
 * MOVMO-542: the dropdown's "Unlink Mabuhay Miles" affordance calls the
 * real DELETE .../loyalty/link and applies the refreshed wire snapshot
 * directly (applyWireTraveler) — no prop from Header needed. Once applied,
 * useLoyaltySession's next render sees loyalty absent and this component
 * simply stops being rendered.
 */
const LoyaltyMemberChip: React.FC<LoyaltyMemberChipProps> = ({
    firstName,
    link,
}) => {
    const [open, setOpen] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const [unlinkError, setUnlinkError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // MOVMO-542: real unlink — DELETE .../loyalty/link, then push the
    // refreshed (now loyalty-less) wire snapshot into the same store
    // the GET /me read feeds, so this chip disappears and the header
    // falls back to the "Link Mabuhay Miles" CTA the instant it resolves.
    const handleUnlink = async () => {
        if (
            !window.confirm(
                "Unlink Mabuhay Miles from your Movmo account? You can relink anytime.",
            )
        ) {
            return;
        }
        setUnlinking(true);
        setUnlinkError(null);
        try {
            const response = await unlinkLoyaltyWithSession("pa");
            applyWireTraveler("pa", response.movmo);
            setOpen(false);
        } catch (err) {
            console.error("[pa] Mabuhay Miles unlink failed:", err);
            setUnlinkError("Couldn't unlink right now. Please try again.");
        } finally {
            setUnlinking(false);
        }
    };

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onMouseDown);
        return () => document.removeEventListener("mousedown", onMouseDown);
    }, []);

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-white/40 bg-white/10 px-3 py-1.5 text-[15px] text-white transition-colors hover:bg-white/20"
            >
                <span aria-hidden="true" style={{ color: "#f5c518" }}>
                    ★
                </span>
                Mabuhay Miles · {firstName}
            </button>

            {open && (
                <div
                    className="absolute right-0 z-10 mt-2 rounded-lg bg-white text-gray-800 shadow-lg"
                    style={{ minWidth: 280 }}
                >
                    <div className="px-4 py-3">
                        <p className="text-sm font-semibold">
                            {firstName} · {link.tier}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                            {link.memberNumber}
                        </p>
                    </div>

                    <div className="px-4 pb-3">
                        <p className="text-sm text-gray-700">
                            {formatMiles(link.milesBalance)} miles
                        </p>
                    </div>

                    <div className="border-t border-gray-100 px-4 py-3">
                        <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-green-500" />
                            <span className="text-sm text-gray-800">
                                Signed in via Movmo
                            </span>
                        </div>
                        <p className="ml-4 mt-0.5 text-xs text-gray-500">
                            Personalization active
                        </p>
                    </div>

                    <div className="px-4 pb-3">
                        <p className="flex items-center gap-1.5 text-xs text-gray-400">
                            <span aria-hidden="true">🔒</span>
                            Miles redemption requires verification
                        </p>
                    </div>

                    <div className="border-t border-gray-100 px-4 py-3">
                        <button
                            type="button"
                            className="text-xs text-gray-400 transition-colors hover:text-gray-600"
                        >
                            Manage in your Movmo account
                        </button>
                    </div>

                    <div className="border-t border-grayWarm-200 px-4 py-3">
                        {unlinkError && (
                            <p className="mb-2 text-xs" style={{ color: "#b42318" }}>
                                {unlinkError}
                            </p>
                        )}
                        <button
                            type="button"
                            disabled={unlinking}
                            onClick={handleUnlink}
                            className="text-xs text-grayWarm-500 transition-colors hover:text-grayWarm-700 disabled:opacity-60"
                        >
                            {unlinking
                                ? "Unlinking…"
                                : "Unlink Mabuhay Miles"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoyaltyMemberChip;
